import { Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Localizable } from './localize-field';

/**
 * Resolve a localizable label (e.g. an action-button label) for the ACTIVE language.
 *
 * Fixes the previous template idiom `(node | localizeField:F) || node[F] || t(key)`, whose
 * fallback order returned the English BASE value before the per-language default — letting an
 * English label (or an admin's English customization) bleed into Spanish. This mirrors the
 * step title/heading resolver (ContextInjector):
 *   1. explicit per-language admin override: node._i18n[lang][field]
 *   2. default language only: the base value when it is a genuine customization
 *      (i.e. it differs from the convention default for the default language)
 *   3. the per-language convention default: Transloco translation of conventionKey
 *   4. the base value as a last resort
 *
 * Impure (like LocalizeFieldPipe) so it re-evaluates on language change.
 */
@Pipe({
  name: 'localizedLabel',
  pure: false,
  standalone: false,
})
export class LocalizedLabelPipe implements PipeTransform {
  constructor(private readonly _translocoService: TranslocoService) {}

  transform<T extends Localizable>(
    node: T | undefined | null,
    field: keyof T & string,
    conventionKey: string,
  ): string {
    if (!node) {
      return '';
    }

    const lang = this._translocoService.getActiveLang();
    const defaultLang = this._translocoService.getDefaultLang();

    // 1. explicit per-language override
    const override = node._i18n?.[lang]?.[field];
    if (override) {
      return override;
    }

    const base = node[field] as unknown as string | undefined;
    const conventionForLang = (this._translocoService.getTranslation(lang) as Record<string, string>)?.[conventionKey];

    // 2. default language: show the base only when it is a real customization
    if (lang === defaultLang && base) {
      const conventionDefault = (this._translocoService.getTranslation(defaultLang) as Record<string, string>)?.[conventionKey];
      const baseIsUncustomized = conventionDefault !== undefined && base === conventionDefault;
      if (!baseIsUncustomized) {
        return base;
      }
    }

    // 3. per-language convention default
    if (conventionForLang) {
      return conventionForLang;
    }

    // 4. base last resort
    return base ?? '';
  }
}
